import { useAuth } from "@/hooks/auth";
import {
  useNotificationsQuery,
  useUpdateSeenAtMutation,
} from "@/hooks/notifications";
import { formatDateTime, getInitials } from "@/utils/misc";
import Avatar from "@ingka/avatar";
import Button from "@ingka/button";
import ListView, { ListViewItem } from "@ingka/list-view";
import bell from "@ingka/ssr-icon/paths/bell";
import questionMarkCircle from "@ingka/ssr-icon/paths/question-mark-circle";
import Tabs, { Tab } from "@ingka/tabs";
import Text from "@ingka/text";
import { useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import {
  Link,
  NavLink,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import { PreferenceSheet } from "../Preferences/PreferenceSheet";
import { ListBoxContainer } from "./ListBoxContainer";
import {
  INSTRUCTION_PAGE_LINKS,
  SIMILARITY_SEARCH_URL,
  SUPPORT_TICKET_SHOPPABLE_REQUEST_URL,
} from "@/constants";
import { sendErrorToSentry } from "../../sentry";

export const GlobalHeader = () => {
  const navigate = useNavigate();
  const currentTabName = getCurrentTabName(useLocation().pathname);
  const { firstName, lastName, isAdmin, isIngkaUser } = useAuth().user;
  const notificationsQuery = useNotificationsQuery();
  const updateSeenAtMutation = useUpdateSeenAtMutation();
  const environment = import.meta.env.VITE_ENV;
  const params = useParams();
  const location = useLocation();
  const lastMarkedAsSeenUploadId = useRef<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const currentUploadId = params.uploadId;
    async function markNotificationAsSeen() {
      try {
        await updateSeenAtMutation.mutateAsync(Number(currentUploadId));
      } catch (e) {
        console.error(`Error occurred while updating notifcation`, e);
        sendErrorToSentry(e, { tags: { feature: "notifications" } });
      }
    }

    if (
      currentUploadId &&
      lastMarkedAsSeenUploadId.current !== currentUploadId &&
      (location.pathname === `/uploads/${currentUploadId}/comments` ||
        location.pathname === `/admin/${currentUploadId}`)
    ) {
      markNotificationAsSeen();
      lastMarkedAsSeenUploadId.current = currentUploadId;
    }
  }, [params.uploadId, location.pathname, updateSeenAtMutation]);

  useEffect(() => {
    if (notificationsQuery.isError) {
      console.error(
        "Error occurred while fetching notifications",
        notificationsQuery.error,
      );
      sendErrorToSentry(notificationsQuery.error, {
        tags: { feature: "notifications" },
      });
    }
  }, [notificationsQuery.isError, notificationsQuery.error]);

  const userNotifications = notificationsQuery.data?.notifications || [];
  const unseenNotificationCount = notificationsQuery.data?.unseenCount || 0;

  const linkButton = (
    <ListBoxContainer
      renderTrigger={(toggleFn) => (
        <Button
          type="tertiary"
          ssrIcon={questionMarkCircle}
          inverseTheme
          iconOnly
          aria-label="Help and support"
          onClick={toggleFn}
        />
      )}
      renderChildren={() => (
        <ListView id="support-list-id" size="small">
          {isIngkaUser && (
            <ListViewItem
              control="link"
              inset
              title="Similarity search"
              href={SIMILARITY_SEARCH_URL}
              newWindow="_blank"
            />
          )}
          <ListViewItem
            control="link"
            inset
            title="Common questions and instructions"
            href={INSTRUCTION_PAGE_LINKS[isIngkaUser ? "ingka" : "external"]}
            newWindow="_blank"
          />
          <ListViewItem
            control="link"
            inset
            title="Create support ticket"
            href={SUPPORT_TICKET_SHOPPABLE_REQUEST_URL}
            newWindow="_blank"
          />
          <ListViewItem
            control="link"
            inset
            title="Request uploaded images shoppable/cropped"
            href={SUPPORT_TICKET_SHOPPABLE_REQUEST_URL}
            newWindow="_blank"
          />
        </ListView>
      )}
    />
  );

  const notificationButton = (
    <ListBoxContainer
      renderTrigger={(toggleFn) => (
        <div className="relative">
          <Button
            type="tertiary"
            ssrIcon={bell}
            inverseTheme
            iconOnly
            aria-label="Notifications"
            onClick={userNotifications.length > 0 ? toggleFn : () => {}} // hiding ListView when there are no notifications.
          />
          {unseenNotificationCount > 0 && (
            <span className="notification-badge">
              {unseenNotificationCount}
            </span>
          )}
        </div>
      )}
      renderChildren={() => (
        <ListView id="notifications-list-id" size="small">
          {userNotifications &&
            userNotifications.map((notification) => (
              <NavLink
                key={notification.id}
                to={
                  isAdmin
                    ? `/admin/${notification.uploadId}`
                    : `/uploads/${notification.uploadId}/comments`
                }
              >
                <div
                  className={classNames(
                    "flex gap-x-space50 border-b border-gray-300 p-space50",
                    {
                      "font-bold": !notification.seenAt,
                      "font-normal": notification.seenAt,
                      "bg-gray-100": notification.seenAt,
                    },
                  )}
                >
                  <Text bodySize="m">{notification.message}</Text>
                  <Text
                    bodySize="s"
                    className="ml-auto w-space550 text-right text-gray-500"
                  >
                    {formatDateTime(notification.createdAt)}
                  </Text>
                </div>
              </NavLink>
            ))}
        </ListView>
      )}
    />
  );

  return (
    <header className="flex items-center justify-between bg-coworker-blue px-space100 py-space75">
      <div className="flex items-center gap-space250">
        <Link to="/">
          <Text headingSize="s" tagName="h1" className="text-coworker-yellow">
            Ingka DAM - Uploader Tool
          </Text>
        </Link>
        <Tabs
          activeTab={currentTabName}
          tabs={[
            <Tab
              key="request_upload_tab"
              id="request_upload_tab"
              tabPanelId="request_upload_tab"
              text={isAdmin ? "Upload" : "Request Upload"}
              className="text-white"
              onClick={() => {
                navigate("/");
              }}
            />,
            <Tab
              key="overview_tab"
              id="overview_tab"
              tabPanelId="overview_tab"
              text="Overview"
              className="text-white"
              onClick={() => navigate("/overview")}
            />,
          ]}
        />
      </div>
      {environment === "dev" && (
        <Text
          headingSize="m"
          tagName="h2"
          className="pr-[18rem] text-center font-bold text-coworker-yellow"
        >
          DEV
        </Text>
      )}
      <ul className="flex h-full cursor-pointer items-center gap-space100">
        <li>{linkButton}</li>
        <li>{notificationButton}</li>
        <li onClick={() => isAdmin && setIsModalOpen(true)}>
          <Avatar
            screenReaderText={`Initials of ${firstName} ${lastName}`}
            text={getInitials(firstName, lastName)}
            size="medium"
            variant="secondary"
            staticColour
          />
        </li>
        {isAdmin && (
          <li>
            <PreferenceSheet
              modalOpen={isModalOpen}
              onModalClosed={() => {
                setIsModalOpen(false);
                queryClient.invalidateQueries({
                  queryKey: ["uploads"],
                });
              }}
            />
          </li>
        )}
      </ul>
    </header>
  );
};

function getCurrentTabName(pathname: string): string {
  if (pathname.startsWith("/overview")) {
    return "overview_tab";
  } else if (!pathname.startsWith("/admin")) {
    return "request_upload_tab";
  }
  return "no_tab";
}
